/**
 * PHASE 10 — user preferences.
 *
 * Typed, validated, persisted, subscribable settings store. Kept deliberately
 * small: display toggles that affect the renderer and label layers.
 *
 * The store is fully testable — storage is injected; validation means a
 * corrupted or stale localStorage blob degrades to defaults instead of
 * crashing the app.
 */

export interface Preferences {
  /** Draw the constellation line network. */
  showConstellationLines: boolean;
  /** Draw the deep-sky catalogue (galaxies/nebulae/clusters). */
  showDeepSky: boolean;
  /** Pin star names / object names to the projected positions. */
  showLabels: boolean;
}

export const DEFAULT_PREFERENCES: Preferences = {
  showConstellationLines: true,
  showDeepSky: true,
  showLabels: true,
};

export const PREFERENCES_STORAGE_KEY = "starlight-sky:preferences";

const BOOLEAN_KEYS: readonly (keyof Preferences)[] = [
  "showConstellationLines",
  "showDeepSky",
  "showLabels",
];

/** Coerce arbitrary parsed JSON into a valid Preferences (defaults win). */
export function normalizePreferences(raw: unknown): Preferences {
  const out: Preferences = { ...DEFAULT_PREFERENCES };
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of BOOLEAN_KEYS) {
      if (typeof obj[key] === "boolean") out[key] = obj[key];
    }
  }
  return out;
}

export type PreferencesListener = (prefs: Preferences) => void;

export class PreferencesStore {
  private state: Preferences;
  private storage: Pick<Storage, "getItem" | "setItem"> | null;
  private listeners = new Set<PreferencesListener>();

  constructor(storage: Pick<Storage, "getItem" | "setItem"> | null = null, initial: Preferences = DEFAULT_PREFERENCES) {
    this.storage = storage;
    this.state = this.restore() ?? { ...initial };
  }

  private restore(): Preferences | null {
    try {
      const raw = this.storage?.getItem(PREFERENCES_STORAGE_KEY);
      if (!raw) return null;
      return normalizePreferences(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private persist(): void {
    try {
      this.storage?.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* storage unavailable or full — non-fatal */
    }
  }

  get(): Preferences {
    return this.state;
  }

  /** Merge a partial update, persist, and notify subscribers. */
  set(partial: Partial<Preferences>): Preferences {
    const merged: Preferences = { ...this.state, ...partial };
    for (const key of BOOLEAN_KEYS) {
      if (typeof merged[key] !== "boolean") merged[key] = DEFAULT_PREFERENCES[key];
    }
    this.state = merged;
    this.persist();
    for (const listener of this.listeners) listener(this.state);
    return this.state;
  }

  /** Restore every setting to its default (e.g. a "reset" UI action). */
  reset(): Preferences {
    return this.set({ ...DEFAULT_PREFERENCES });
  }

  subscribe(listener: PreferencesListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}